import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam';
import { fg } from '@confluence/feature-gating';

import { SpacePagesQuery } from './SpacePagesQuery.graphql';
import { PagesSpaceIdQuery } from './hooks/PagesSpaceIdQuery.graphql';
import { SpacePagesPersistenceQuery } from './SpacePagesPersistenceQueries.graphql';
import {
	ConfluenceSearchContentStatus,
	ConfluenceContentSearchScope,
	ConfluenceContentSortingParameter as PagesSortField,
	SortOrder as PagesSortOrder,
} from './__types__/SpacePagesQuery';
import type { PreloadSpacePagesOptions } from './types';
import { SearchResultsAGGSearchQuery } from './SearchResultsAGGSearchQuery';
import type {
	SearchResultsAGGSearchQuery as SearchResultsAGGSearchQuery$data,
	SearchResultsAGGSearchQueryVariables as SearchResultsAGGSearchQuery$variables,
} from './__types__/SearchResultsAGGSearchQuery';
import { getPreloadAggSpacePagesQueryVariables, SPACE_PAGES_QUERY_LIMIT } from './utils/queryUtils';

export const preloadSpacePages = (options: PreloadSpacePagesOptions) => {
	if (fg('cc-agg-migration_space-pages-to-agg')) {
		return preloadSpacePagesAgg(options);
	}
	return preloadSpacePagesCCGraph(options);
};

const preloadSpacePagesAgg = (options: PreloadSpacePagesOptions) => {
	const variables = getPreloadAggSpacePagesQueryVariables(options);
	const { spaceKey, isLoggedIn, spaceId = '' } = options;
	return waitForAllReturnWhenError([
		queryAgg<SearchResultsAGGSearchQuery$data, SearchResultsAGGSearchQuery$variables>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: SearchResultsAGGSearchQuery,
			variables,
		}),
		...(!spaceId
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: PagesSpaceIdQuery,
						variables: { spaceKey },
					}),
				]
			: []),
		...(isLoggedIn
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: SpacePagesPersistenceQuery,
						variables: { spaceKey },
					}),
				]
			: []),
	]);
};

export const preloadSpacePagesCCGraph = ({
	spaceKey,
	queryParams,
	userId,
	isLicensed,
	isLoggedIn,
	spaceId = '',
	isWhiteboardEnabled = false,
	isDatabaseEnabled = false,
}: PreloadSpacePagesOptions) => {
	const pagesFilter = getSingleParam(queryParams, 'pagesFilter');
	const searchText = getSingleParam(queryParams, 'text') || '';
	const variables = {
		first: 24,
		maxNumberOfResults: SPACE_PAGES_QUERY_LIMIT,
		searchText,
		filters: {
			spaces: { spaceKeys: [spaceKey] },
			titleMatchOnly: { titleMatchOnly: true },
			...(pagesFilter === 'worked-on' &&
				userId &&
				isLicensed && {
					contributors: { accountIds: [userId] },
				}),
			...(pagesFilter === 'starred' &&
				isLicensed && {
					labels: [{ namespace: 'MY', name: 'favourite' }],
				}),
			statuses: {
				statuses: [ConfluenceSearchContentStatus.CURRENT, ConfluenceSearchContentStatus.DRAFT],
			},
		},
		sortBy: [
			{
				field: PagesSortField.LAST_MODIFIED_DATE,
				order: PagesSortOrder.DESC,
			},
		],
		isLicensed: Boolean(isLicensed),
		includeRecommended: false,
		spaceId,
		scopes: [
			ConfluenceContentSearchScope.PAGE,
			...(isWhiteboardEnabled ? [ConfluenceContentSearchScope.WHITEBOARD] : []),
			...(isDatabaseEnabled ? [ConfluenceContentSearchScope.DATABASE] : []),
			ConfluenceContentSearchScope.EMBED,
			ConfluenceContentSearchScope.FOLDER,
		],
	};

	return waitForAllReturnWhenError([
		query({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: SpacePagesQuery,
			variables,
		}),
		...(!spaceId
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: PagesSpaceIdQuery,
						variables: { spaceKey },
					}),
				]
			: []),
		...(isLoggedIn
			? [
					query({
						/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
						query: SpacePagesPersistenceQuery,
						variables: { spaceKey },
					}),
				]
			: []),
	]);
};
